import { useContext, useState } from 'react';
import usePageType from './use-page-type';
import { graphqlSdk } from '../Utils/graphql-client';
import {
  bookingState,
  BookingStateCombinedState,
} from '../Pages/Booking/client-state/booking-state-context';
import {
  bookingEditState,
  BookingEditStateCombinedState,
} from '../Pages/BookingEdit/client-state/booking-edit-state-context';
import { orderReducerActionTypes } from '../redusers/order-reducer';
import useBookingTracking, { bookingTrackFunction } from './useBookingTracking';
import useBookingEditTracking, {
  bookingEditTrackFunction,
} from './use-booking-edit-tracking';

interface StateOrder {
  itemId: string;
  quantity: number;
}

const useRemoveItem = (itemId: string) => {
  const pageType = usePageType();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // This hook is currently only used on the booking and edit-booking pages
  const { state: stateBooking, dispatch: dispatchBooking } = useContext(bookingState);
  const { state: stateBookingEdit, dispatch: dispatchBookingEdit } =
    useContext(bookingEditState);
  const bookingTrack = useBookingTracking();
  const bookingEditTrack = useBookingEditTracking();

  let state: BookingStateCombinedState['state'] | BookingEditStateCombinedState['state'];
  let dispatch:
    | BookingStateCombinedState['dispatch']
    | BookingEditStateCombinedState['dispatch'];
  let track: bookingTrackFunction | bookingEditTrackFunction;

  switch (pageType) {
    case 'booking': {
      state = stateBooking;
      dispatch = dispatchBooking;
      track = bookingTrack;
      break;
    }
    case 'edit-booking': {
      state = stateBookingEdit;
      dispatch = dispatchBookingEdit;
      track = bookingEditTrack;
      break;
    }
    default: {
      state = stateBooking;
      track = bookingTrack;
      break;
    }
  }

  const removeItem = async () => {
    const ItemToRemove = state.order.find((orderItem) => orderItem.itemId === itemId);

    try {
      setLoading(true);
      const newOrder: StateOrder[] = state.order
        .filter((orderItem: StateOrder) => {
          return orderItem.itemId !== itemId;
        })
        .map((orderItem: StateOrder) => {
          // sometimes (in edit booking flow) the order items have more properties (full item object)
          // hence we strip here
          return {
            quantity: orderItem.quantity,
            itemId: orderItem.itemId,
          };
        });

      if (pageType === 'booking' && state.booking.id) {
        /**
         * For create booking flow
         */
        const result = await graphqlSdk.setBookingAddons({
          bookingId: state.booking.id,
          items: newOrder,
        });

        const updatedOrder = result.setBookingAddons.addons.map((addon) => {
          return {
            itemId: addon.itemId,
            quantity: addon.quantity,
          };
        });

        dispatch({
          type: orderReducerActionTypes.UPDATE_ORDER,
          payload: updatedOrder,
        });

        track({
          type: 'REMOVE_ITEM_FROM_CART',
          item: { id: itemId, quantity: ItemToRemove?.quantity },
        });
      } else if (pageType === 'edit-booking' && state.booking.reservationId) {
        /**
         * For edit booking flow
         */
        const result = await graphqlSdk.setReservtionItems({
          reservationId: state.booking.reservationId,
          items: newOrder,
        });

        const updatedOrder = result.setReservationItems.map((item) => {
          return {
            itemId: item.itemId,
            quantity: item.quantity,
          };
        });

        dispatch({
          type: orderReducerActionTypes.UPDATE_ORDER,
          payload: updatedOrder,
        });

        track({
          type: 'REMOVE_ITEM_FROM_CART',
          item: { id: itemId, quantity: ItemToRemove?.quantity },
        });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(true);
    }
  };

  return {
    removeItem,
    loading,
    error,
  };
};

export default useRemoveItem;
